<template>
  <vs-popup :active="editingBudgetType" :buttons-hidden="true">
    <vs-row>
      <div class="vx-col w-full text-left">
        <h3 class="font-medium">
          {{ $t('Confirmation') }}
        </h3>
        <p class="text-gray-500 mt-2">
          If your campaign does not have a specific end date or a total budget, you need to set a budget.
        </p>
      </div>

      <div class="inputs-parent vx-col md:w-1/2 sm:w-full pr-5 my-5" v-for="(adsetBudgetItem, index) in adsetBudgets" :key="'adsets-' + index">
        <h5 class="font-normal text-lg mb-2">Adset {{ adsetBudgetItem.name }}</h5>
        <p class="text-gray-500 mt-2 mb-2 text-sm">{{minAllowedBudgetMsg}}</p>

        <vx-input-group>
          <template slot="prepend">
            <div class="prepend-text dollar-sign" style="margin-top: 0;">
              <span>$</span>
            </div>
          </template>

          <div class="vx-input-group-default flex-grow">
            <div class="vs-component vs-con-input-label vs-input w-full vs-input-primary">
              <div class="vs-con-input">
                <input class="vs-inputx vs-input--input" min="0" type="number" @blur="validateCampaignBudget($event, adsetBudgetItem)" @input="updateAdsetBudgetItem(adsetBudgetItem.id, $event)" :value="newAdsetBudgets[index].budget" :placeholder="$t('Budget')">
              </div>
              <span></span>
            </div>
          </div>
        </vx-input-group>
      </div>

      <div class="vx-col w-full mt-10 text-right">
        <vs-button class="mr-4 cancel-button" color="primary" @click="cancelNewBudgets" type="border">{{ $t('Cancel') }}</vs-button>
        <vs-button class="confirm-update-button" color="primary" @click="confirmNewBudgetAndUpdate" type="filled" :disabled="areInputsInvalid">{{ $t('Confirm & Update') }}</vs-button>
      </div>
    </vs-row>

    <speakol-response-popup
      v-on:hide-response-popup="errorPopup.show = false"
      :responsePopupData="errorPopup"
    />
  </vs-popup>
</template>

<script>
import validateBudget from '@/validators/budget'

export default {
  props: {
    adsetBudgets: {
      type: Array,
      required: false,
      default: () => []
    },
    editingBudgetType: {
      type: Boolean,
      required: true
    },
    objective: {
      type: Object,
      required: true
    },
    startDate: {
      type: String,
      required: false
    },
    endDate: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      newAdsetBudgets: [],
      errorPopup: {
        show: false,
        type: 'error',
        body: ''
      }
    }
  },
  computed: {
    minAllowedBudgetMsg () {
      const validatedBudget = validateBudget(0, this.objective.min_daily_budget, this.startDate, this.endDate)
      return validatedBudget.msg
    },
    areInputsInvalid () {
      if (!this.adsetBudgets || !this.adsetBudgets.length) {
        return false
      }

      for (let i = 0; i < this.adsetBudgets.length; i++) {
        const adsetBudget = this.adsetBudgets[i]
        const adsetBudgetValue = this.newAdsetBudgets.find(i => i.id === adsetBudget.id)
        const budget = parseFloat(adsetBudgetValue.budget)

        if (
          !budget ||
          Number.isNaN(budget) ||
          (adsetBudgetValue && budget <= parseFloat(adsetBudget.min_budget)) ||
          !this.budgetValidator(budget).valid
        ) {
          return true
        }
      }

      return false
    }
  },
  created () {
    this.adsetBudgets.forEach((i) => {
      this.newAdsetBudgets.push({
        id: i.id,
        budget: i.budget
      })
    })
  },
  methods: {
    validateCampaignBudget ($event, adsetBudgetItem) {
      const value = parseFloat($event.target.value || 0)

      const budgetValidator = this.budgetValidator(value)

      const budgetType = this.endDate ? 'budget' : 'daily budget'

      let msg = ''
      if (value === 0) {
        msg = `The ${budgetType} field is required`
      } else if (value <= parseFloat(adsetBudgetItem.min_budget)) {
        msg = `The ${budgetType} can not be less than or equal the bid value`
      } else if (!budgetValidator.valid) {
        msg = budgetValidator.msg
      }

      if (msg) {
        this.errorPopup.show = true
        this.errorPopup.body = msg
      }
    },

    budgetValidator (value) {
      return validateBudget(
        value,
        this.objective.min_daily_budget,
        this.startDate,
        this.endDate
      )
    },

    updateAdsetBudgetItem (adsetId, $event) {
      let value = $event.target.value || null

      if (Number.isNaN(value)) {
        value = null
      }

      const index = this.newAdsetBudgets.findIndex(i => i.id === adsetId)

      if (index > -1) {
        if (!value) {
          this.newAdsetBudgets[index].budget = null
          return
        }

        value = parseFloat(value)

        if (value > 99999.99) {
          this.errorPopup.show = true
          this.errorPopup.body = 'The value should be less than  99999.99'
          return
        }

        const fraction = value.toString().split('.')[1] || ''

        if (fraction.length > 2) {
          this.errorPopup.show = true
          this.errorPopup.body = 'The Decimals should be up to 2 digits'
          return
        }

        this.newAdsetBudgets[index].budget = value
        return
      }

      this.newAdsetBudgets[index].budget = 0.00
    },
    cancelNewBudgets () {
      this.$emit('cancel-new-budget', true)
    },
    confirmNewBudgetAndUpdate () {
      this.$emit('confirm-new-budget-and-update', this.newAdsetBudgets)
    }
  }
}
</script>

<style lang="scss">

  .cancel-button {
    color: black !important;
    border-radius: 3px !important;
    border: 2px solid #d9d9d9 !important;
    padding: 7px 30px !important;
  }

  .cancel-button:hover {
    background: transparent !important;
  }

  .confirm-update-button {
    background: #009bde !important;
    border-radius: 3px !important;
    color: white !important;
    padding: 8px 11px !important;
  }

  @media (max-width: 576px) {
    .inputs-parent {
      width: 100%;
      padding-right: 0 !important;
    }
  }
</style>
